import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Link as LinkIcon, Linkedin, Twitter, Facebook } from 'lucide-react';
import { TrendingSection } from './elements/TrendingSection';

const NewsDetailPage: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumb */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pt-24 pb-8">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 transition-colors"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            Home
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <Link
            to="/news"
            className="text-gray-500 hover:text-gray-700 transition-colors"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            News
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span
            className="text-gray-700"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            Platform & Campsite Updates
          </span>
        </div>
      </div>

      {/* Article Header */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <h1
            className="mb-8"
            style={{
              fontFamily: 'Poppins',
              fontSize: '56px',
              fontWeight: 700,
              lineHeight: '64px',
              color: '#000000'
            }}
          >
            New Trails Unlocked: Explore Two Fresh Campsites Near Plover Cove!
          </h1>

          {/* Author Info and Social Share */}
          <div className="flex items-center justify-between">
            {/* Left: Author Info */}
            <div className="flex items-center gap-4">
              {/* Author Avatar */}
              <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center">
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#000000'
                  }}
                >
                  👓
                </span>
              </div>

              {/* Author Name and Date */}
              <div>
                <p
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#000000'
                  }}
                >
                  Trancy CAO
                </p>
                <div className="flex items-center gap-2">
                  <span
                    style={{
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#667479'
                    }}
                  >
                    2025.10.07
                  </span>
                  <span style={{ color: '#667479' }}>•</span>
                  <span
                    style={{
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#667479'
                    }}
                  >
                    5 min read
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Social Share Icons */}
            <div className="flex items-center gap-3">
              <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                <LinkIcon className="w-5 h-5 text-gray-700" />
              </button>
              <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                <Linkedin className="w-5 h-5 text-gray-700" />
              </button>
              <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                <Twitter className="w-5 h-5 text-gray-700" />
              </button>
              <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                <Facebook className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image - Wider than article content */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-12">
        <img
          src="https://placehold.co/1200x600/e2e8f0/64748b?text=Camping+Article"
          alt="Article featured"
          className="w-full h-96 object-cover rounded-2xl"
        />
      </div>

      {/* Article Content */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-12">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Introduction Section */}
          <div>
            <h2
              className="mb-4"
              style={{
                fontFamily: 'Poppins',
                fontSize: '24px',
                fontWeight: 700,
                color: '#000000'
              }}
            >
              Introduction
            </h2>
            <p
              className="mb-4"
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              Hong Kong's outdoor recreation landscape just got even better! Two brand-new camping trails near Plover Cove Reservoir have officially opened, offering nature enthusiasts fresh routes to explore. These trails feature improved signage, designated safety zones, and real-time occupancy tracking powered by Alpha AI's smart monitoring system.
            </p>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              Whether you're a seasoned camper or just starting out, these new trails provide the perfect opportunity to disconnect from the city and reconnect with nature.
            </p>
          </div>

          {/* Body Paragraph */}
          <div>
            <p
              className="mb-4"
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              The development of these trails is part of a broader initiative by the Agriculture, Fisheries and Conservation Department (AFCD) to expand sustainable outdoor recreation across Hong Kong's country parks.
            </p>
          </div>

          {/* Image with Caption */}
          <div>
            <img
              src="https://placehold.co/1200x600/e2e8f0/64748b?text=Hong+Kong+Campsite"
              alt="Hong Kong Campsite"
              className="w-full h-96 object-cover rounded-2xl mb-3"
            />
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                fontStyle: 'italic',
                color: '#667479',
                textAlign: 'center'
              }}
            >
              Hong Kong Campsite
            </p>
          </div>

          {/* Body Paragraph with Bold Text */}
          <div>
            <p
              className="mb-4"
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              <span style={{ fontWeight: 600, color: '#000000' }}>What makes these trails special?</span> Each trail has been carefully designed with environmental sustainability in mind. The routes minimize ecological impact while maximizing the camping experience with scenic viewpoints, water access points, and sheltered rest areas.
            </p>
          </div>

          {/* Block Quote */}
          <div
            className="border-l-4 pl-6 py-2"
            style={{
              borderColor: '#000000',
              backgroundColor: '#F9FAFB'
            }}
          >
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: '32px',
                color: '#000000',
                fontStyle: 'italic'
              }}
            >
              "These new trails represent our commitment to making Hong Kong's natural beauty more accessible while ensuring its protection for future generations."
            </p>
            <p
              className="mt-2"
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              — AFCD Spokesperson
            </p>
          </div>

          {/* More Body Content */}
          <div>
            <p
              className="mb-4"
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              Both trails are now available for booking through the Alpha AI platform. Real-time occupancy data helps campers plan their visits during less crowded periods, ensuring a more peaceful outdoor experience.
            </p>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              The smart monitoring system also enhances safety by alerting authorities to any emergencies or unusual activities, giving campers peace of mind as they enjoy the wilderness.
            </p>
          </div>

          {/* Conclusion Section */}
          <div>
            <h2
              className="mb-4"
              style={{
                fontFamily: 'Poppins',
                fontSize: '24px',
                fontWeight: 700,
                color: '#000000'
              }}
            >
              Conclusion
            </h2>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#667479'
              }}
            >
              With the opening of these two new trails, Hong Kong's camping scene continues to grow and evolve. Whether you're looking for a weekend getaway or a day hike with stunning reservoir views, these trails offer something for everyone. Don't forget to check real-time availability on our platform before heading out, and always practice Leave No Trace principles to help preserve these beautiful spaces.
            </p>
          </div>

          {/* Share This Post Section with Category Badge */}
          <div>
            <h3
              className="mb-4"
              style={{
                fontFamily: 'Poppins',
                fontSize: '18px',
                fontWeight: 600,
                color: '#000000'
              }}
            >
              Share this post
            </h3>
            <div className="flex items-center justify-between">
              {/* Left: Social Share Icons */}
              <div className="flex items-center gap-3">
                <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                  <LinkIcon className="w-5 h-5 text-gray-700" />
                </button>
                <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Linkedin className="w-5 h-5 text-gray-700" />
                </button>
                <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Twitter className="w-5 h-5 text-gray-700" />
                </button>
                <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Facebook className="w-5 h-5 text-gray-700" />
                </button>
              </div>

              {/* Right: Category Badge */}
              <span
                className="px-6 py-2 bg-black rounded-full"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#FFFFFF'
                }}
              >
                Platform & Campsite Updates
              </span>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Author Info */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-yellow-400 flex items-center justify-center">
              <span
                style={{
                  fontFamily: 'Inter',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#000000'
                }}
              >
                👓
              </span>
            </div>
            <div>
              <p
                style={{
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#000000'
                }}
              >
                Trancy CAO
              </p>
              <p
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#667479'
                }}
              >
                Alpha AI
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trending Section */}
      <TrendingSection />
    </div>
  );
};

export default NewsDetailPage;
